"use client";

import * as React from "react";
import { cn } from "@/lib/utils/index";

const TabsContext = React.createContext(null);

function Tabs({
  value: controlledValue,
  defaultValue,
  onValueChange,
  className,
  children,
  ...props
}) {
  const [uncontrolledValue, setUncontrolledValue] =
    React.useState(defaultValue);
  const value =
    controlledValue !== undefined ? controlledValue : uncontrolledValue;

  const handleChange = React.useCallback(
    (nextValue) => {
      if (controlledValue === undefined) {
        setUncontrolledValue(nextValue);
      }
      onValueChange?.(nextValue);
    },
    [controlledValue, onValueChange],
  );

  const contextValue = React.useMemo(
    () => ({ value, onValueChange: handleChange }),
    [value, handleChange],
  );

  return (
    <TabsContext.Provider value={contextValue}>
      <div className={className} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

function TabsList({ className, ...props }) {
  return (
    <div
      role="tablist"
      data-slot="tabs-list"
      className={cn(
        "inline-flex items-center gap-1 rounded-radius border border-border bg-muted/50 p-1 text-sm",
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({ value, className, children, ...props }) {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error("TabsTrigger must be used within Tabs.");
  }
  const active = context.value === value;

  return (
    <button
      type="button"
      role="tab"
      data-slot="tabs-trigger"
      data-state={active ? "active" : "inactive"}
      aria-selected={active}
      className={cn(
        "rounded-radius px-3 py-1.5 font-medium text-muted-foreground transition hover:text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
        className,
      )}
      onClick={() => context.onValueChange(value)}
      {...props}
    >
      {children}
    </button>
  );
}

function TabsContent({ value, className, children, ...props }) {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error("TabsContent must be used within Tabs.");
  }
  const active = context.value === value;

  if (!active) return null;

  return (
    <div
      role="tabpanel"
      data-slot="tabs-content"
      data-state={active ? "active" : "inactive"}
      className={cn("mt-3", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
